import { History, MapPin, User, CalendarDays, Package } from 'lucide-react';

import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { CONDICION_META } from '@/lib/condicion';
import type { EstadoItem, InventarioItem } from '@/services/api/inventario.api';

/**
 * Resumen del estado actual de un activo.
 *
 * Responde de un vistazo las tres preguntas que antes obligaban a abrir el
 * historial: en qué estado está, quién lo tiene y desde cuándo. Trabaja con la
 * fila que la lista ya tiene en memoria, así que abre sin esperar red; el
 * historial completo sigue estando a un clic.
 */

const ESTADO_META: Record<EstadoItem, { label: string; badge: string; dot: string }> = {
  EN_BODEGA:     { label: 'En bodega',  badge: 'bg-emerald-50 text-emerald-700 ring-emerald-200', dot: 'bg-emerald-500' },
  ASIGNADO:      { label: 'Asignado',   badge: 'bg-primary-50 text-primary-700 ring-primary-200', dot: 'bg-primary-500' },
  EN_REPARACION: { label: 'Reparación', badge: 'bg-amber-50 text-amber-800 ring-amber-200',       dot: 'bg-amber-500' },
  DADO_DE_BAJA:  { label: 'De baja',    badge: 'bg-slate-100 text-slate-600 ring-slate-200',      dot: 'bg-slate-400' },
};

interface Props {
  item: InventarioItem | null;
  onOpenChange: (open: boolean) => void;
  /** Abre la línea de tiempo completa del activo. */
  onVerHistorial: (idItem: string) => void;
}

export function ResumenActivoDialog({ item, onOpenChange, onVerHistorial }: Props) {
  if (!item) return null;

  const estado = ESTADO_META[item.estado_actual];
  const condicion = item.condicion ? CONDICION_META[item.condicion] : null;
  const CondicionIcon = condicion?.icon;

  const descripcion = item.especificacion ?? item.modelo ?? item.nombre_glpi ?? null;
  const asignado = item.empleado_asignado;
  const espacio = item.espacio_asignado;

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-start gap-3 text-left">
            <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
              <Package className="h-4 w-4" />
            </span>
            <span className="min-w-0">
              <span className="block font-mono text-base font-semibold tracking-tight">
                {item.serial_global}
              </span>
              {(item.tipo || descripcion) && (
                <span className="mt-0.5 block text-xs font-normal capitalize text-muted-foreground">
                  {[item.tipo?.toLowerCase(), descripcion].filter(Boolean).join(' · ')}
                </span>
              )}
            </span>
          </DialogTitle>
        </DialogHeader>

        {/* ── Estado y condición ── */}
        <div className="flex flex-wrap items-center gap-2">
          <span className={cn(
            'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ring-1',
            estado.badge,
          )}>
            <span className={cn('h-1.5 w-1.5 rounded-full', estado.dot)} />
            {estado.label}
          </span>

          {condicion && CondicionIcon && (
            <span className={cn(
              'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ring-1',
              condicion.badge,
            )}>
              <CondicionIcon className="h-3 w-3" />
              {condicion.label}
            </span>
          )}

          <span className="ml-auto rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            {item.origen_datos}
          </span>
        </div>

        <Separator />

        {/* ── Quién lo tiene ── */}
        <dl className="space-y-3 text-sm">
          <Campo icon={User} etiqueta="Responsable">
            {asignado ? (
              <span className="font-medium text-foreground">
                {asignado.nombres}
                <span className="ml-1.5 font-mono text-xs font-normal text-muted-foreground">
                  {asignado.documento}
                </span>
              </span>
            ) : espacio ? (
              <span className="font-medium text-foreground">{espacio.nombre}</span>
            ) : (
              <span className="text-muted-foreground">Sin asignar</span>
            )}
          </Campo>

          <Campo icon={CalendarDays} etiqueta="Entregado">
            {asignado && item.fecha_asignacion ? (
              <span className="text-foreground">
                {formatFecha(item.fecha_asignacion)}
                <span className="ml-1.5 text-xs text-muted-foreground">
                  ({hace(item.fecha_asignacion)})
                </span>
              </span>
            ) : (
              <span className="text-muted-foreground">
                {asignado ? 'Sin registro de entrega' : '—'}
              </span>
            )}
          </Campo>

          <Campo icon={MapPin} etiqueta="Ubicación">
            {item.ubicacion
              ? <span className="text-foreground">{item.ubicacion}</span>
              : <span className="text-muted-foreground">Sin registrar</span>}
          </Campo>
        </dl>

        <DialogFooter className="sm:justify-between">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              onOpenChange(false);
              onVerHistorial(item.id_item_maestro);
            }}
          >
            <History className="mr-1.5 h-4 w-4" />
            Ver historial
          </Button>
          <Button size="sm" onClick={() => onOpenChange(false)}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Campo({
  icon: Icon, etiqueta, children,
}: {
  icon: typeof User;
  etiqueta: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground/70" />
      <div className="min-w-0 flex-1">
        <dt className="text-xs text-muted-foreground">{etiqueta}</dt>
        <dd className="mt-0.5 break-words">{children}</dd>
      </div>
    </div>
  );
}

function formatFecha(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('es-CO', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch {
    return iso;
  }
}

/** "hace 3 meses" — da escala sin obligar a restar fechas mentalmente. */
function hace(iso: string): string {
  const dias = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);

  if (!Number.isFinite(dias)) return '';
  if (dias < 0) return 'fecha futura';
  if (dias === 0) return 'hoy';
  if (dias === 1) return 'ayer';
  if (dias < 30) return `hace ${dias} días`;

  const meses = Math.floor(dias / 30);
  if (meses < 12) return `hace ${meses} ${meses === 1 ? 'mes' : 'meses'}`;

  const anios = Math.floor(dias / 365);
  return `hace ${anios} ${anios === 1 ? 'año' : 'años'}`;
}
